import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { SelectInstance } from 'react-select';
import { toast } from 'react-toastify';
import { updateWorkplace } from 'services/registration';
import { APIErrors } from 'types/api';
import { Workplace } from 'views/employee-management/useEmployees';
import { schema } from './validation';

export interface EditUserDataValue {
    id: number | null;
    workplace_name: string;
    department: DepartmentOption | null | string;
    workplace_address_postcode: string;
    workplace_address_city: string;
    workplace_address_street: string;
    workplace_address_number: string;
};

export interface UseEditWorkplaceProps {
    workplaceId: number;
    adminWorkplaces: Workplace[];
    onCancel: (reload?: boolean) => void;
};

export type DepartmentOption = {
    label: string;
    value: number;
};

export const useEditWorkplace = ({ workplaceId, adminWorkplaces, onCancel }: UseEditWorkplaceProps) => {
    const [postcode, setPostcode] = useState('');
    const [city, setCity] = useState('');
    const [workplaceName, setWorkplaceName] = useState('');
    const [creating, setCreating] = useState(false);
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [departmentOptions, setDepartmentOptions] = useState<DepartmentOption[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<DepartmentOption | null>(null);
    const newDepartmentInputRef = useRef<HTMLInputElement>(null);
    const asyncSelectRef = useRef<SelectInstance<DepartmentOption>>(null);
    const wasCreating = useRef(false);

    const form = useForm<EditUserDataValue>({
        resolver: yupResolver(schema),
        defaultValues: {
            id: null,
            workplace_name: '',
            department: '',
            workplace_address_postcode: '',
            workplace_address_city: '',
            workplace_address_street: '',
            workplace_address_number: '',
        }
    });

    const { register, handleSubmit, setError, setValue, control, formState: { errors } } = form;

    useEffect(() => {
        if (!workplaceId || !adminWorkplaces) return;
        const workplace = adminWorkplaces.find(w => w.id === workplaceId);
        if (!workplace) return;

        setWorkplaceName(workplace.workplace_name);

        setValue('workplace_name', workplace.workplace_name || '');
        setValue('workplace_address_postcode', workplace.workplace_address_postcode || '');
        setValue('workplace_address_city', workplace.workplace_address_city || '');
        setValue('workplace_address_street', workplace.workplace_address_street || '');
        setValue('workplace_address_number', workplace.workplace_address_number || '');
        setValue('id', workplaceId || null);
    }, [workplaceId, adminWorkplaces, setValue]);

    const editWorkplaceMut = useMutation<
        AxiosResponse<boolean>,
        AxiosError<APIErrors>,
        EditUserDataValue
    >(
        (data) => updateWorkplace(workplaceId, {
            workplace_name: data.workplace_name,
            workplace_address_city: data.workplace_address_city,
            workplace_address_street: data.workplace_address_street,
            workplace_address_number: data.workplace_address_number,
            workplace_address_postcode: data.workplace_address_postcode,
        }),
        {
            onSuccess: () => {
                toast.success('Dane placówki zaktualizowane');
                onCancel(true);
            },
            onError: (error) => {
                const formErrors = error?.response?.data?.errors;
                if (formErrors) {
                    Object.entries(formErrors).forEach(([key, value]) => {
                        setError(key as keyof EditUserDataValue, {
                            message: value?.[0],
                        });
                    });
                }
            },
        }
    );

    const onSubmit = (data: EditUserDataValue) => {
        editWorkplaceMut.mutate(data);
    };

    const formatZipCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 5) value = value.slice(0, 5);
        if (value.length > 2) value = `${value.slice(0, 2)}-${value.slice(2)}`;
        e.target.value = value;
        setPostcode(e.target.value);
        setValue('workplace_address_postcode', e.target.value);
    };

    const onChangeCity = (selected: { value: string; label: string } | null) => {
        setCity(selected?.value || '');
        setValue('workplace_address_city', selected?.value || '');
    };

    useEffect(() => {
        if (creating) {
            setTimeout(() => newDepartmentInputRef.current?.focus(), 0);
        }
        if (wasCreating.current && !creating) {
            setTimeout(() => asyncSelectRef.current?.focus(), 0);
        }
        wasCreating.current = creating;
    }, [creating]);
    
    return {
        form,
        register,
        handleSubmit,
        errors,
        control,
        onSubmit,
        formatZipCode,
        onChangeCity,
        postcode,
        city,
        workplaceName,
        setValue,
        newDepartmentName, 
        setNewDepartmentName, 
        creating, 
        setCreating,
        newDepartmentInputRef,
        asyncSelectRef,
        selectedDepartment, 
        setSelectedDepartment, 
        departmentOptions, 
        setDepartmentOptions
    };
};